import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useFloating, useInteractions, useClick, useRole, useDismiss, FloatingPortal, FloatingFocusManager } from '@floating-ui/react';
import * as Styled from './SocialVideoDownload.styled';
import { FileDownload, Close, PlayCircle } from '../../../../svgs';
import { setColorWithOpacityCss } from '../../../../utils/setColorWithOpacityCss';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * The `SocialVideoDownload` component provides a button that allows users to download
 * a social video for sharing on social media platforms.
 */
export const SocialVideoDownload = ({
  videoUrl,
  downloadButtonLabel,
  showPreview = false,
  previewTitle,
  previewDescription,
  showDownloadInPreview = false,
  alignXPreset,
  cssBackgroundColor,
  cssBackgroundColorOpacity,
  cssBorderRadius,
  cssBorderColor,
  cssBorderWidth,
  cssColor,
  ...rest
}) => {
  const {
    t
  } = useTranslation('blocks.socialLinks');
  const [isLoading, setIsLoading] = useState(false);
  const [isVideoExpanded, setIsVideoExpanded] = useState(false);
  const [thumbnailDataUrl, setThumbnailDataUrl] = useState('');
  const [shouldPlayVideo, setShouldPlayVideo] = useState(false);
  const videoRef = useRef(null);

  // Set up floating UI
  const {
    refs,
    context
  } = useFloating({
    open: isVideoExpanded,
    onOpenChange: setIsVideoExpanded
  });

  // Set up interactions
  const click = useClick(context);
  const dismiss = useDismiss(context, {
    outsidePressEvent: 'mousedown',
    escapeKey: true
  });
  const role = useRole(context);
  const {
    getReferenceProps,
    getFloatingProps
  } = useInteractions([click, dismiss, role]);
  const handleCloseVideo = useCallback(() => {
    setIsVideoExpanded(false);
  }, []);
  const handlePlayClick = useCallback(() => {
    setIsVideoExpanded(true);
    setShouldPlayVideo(true);
  }, []);

  // Handle video focus and play when video becomes expanded
  useEffect(() => {
    if (isVideoExpanded && shouldPlayVideo && videoRef.current) {
      videoRef.current.focus();
      videoRef.current.play().catch(() => {
        // Silently handle play errors - common on mobile devices
      });
      setShouldPlayVideo(false); // Reset the flag
    }
  }, [isVideoExpanded, shouldPlayVideo]);

  /**
   * Use javascript to download the video.
   *
   * Using a plain `<a>` tag is simpler, but it typically opens the video
   * and starts playing it instead of initiating a download.
   */
  const handleDownload = useCallback(async () => {
    // Set loading state to true when download starts
    setIsLoading(true);

    // We already check for videoUrl earlier, but adding a guard clause for safety
    if (!videoUrl) return;
    try {
      // Use fetch but with a timeout to avoid hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      // First check if the video is accessible
      await fetch(videoUrl, {
        method: 'GET',
        signal: controller.signal,
        // Request only the first byte to verify the video URL is accessible
        headers: {
          Range: 'bytes=0-0'
        }
      });

      // Clear the timeout since we got a response
      clearTimeout(timeoutId);

      // Now fetch the full file
      const response = await fetch(videoUrl);
      const blob = await response.blob();

      // Create a temporary anchor to download the video
      const a = document.createElement('a');
      const blobUrl = URL.createObjectURL(blob);
      a.href = blobUrl;
      // TODO: add campaign name to file name
      a.download = 'social-video.mp4';
      document.body.appendChild(a);
      a.click();

      // Clean up
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);

      // Reset loading state
      setIsLoading(false);
    } catch {
      // Reset loading state on error, TODO: improved error handling, need designs
      setIsLoading(false);
    }
  }, [videoUrl]);

  /**
   * Generate a thumbnail from the video.
   *
   * This implementation avoids loading the actual video until the preview is opened,
   * which prevents media keys from controlling the hidden video.
   */
  useEffect(() => {
    if (videoUrl && !thumbnailDataUrl && !isVideoExpanded) {
      // Create a temporary video element to generate the thumbnail
      const tempVideo = document.createElement('video');

      // Required to avoid CORS errors when drawing video to canvas for thumbnail generation
      tempVideo.crossOrigin = 'anonymous';

      /**
       * When video has loaded enough data to be playable, seek to 1-second mark
       * to avoid capturing black frames or intro screens at the beginning
       */
      const handleCanPlay = () => {
        try {
          tempVideo.currentTime = 1;
        } catch {
          /*
           * Some browsers may throw if seeking before metadata is loaded
           * In this case, we'll capture the frame as soon as possible
           */
        }
      };

      /**
       * After seeking to the desired position, capture the current video frame,
       * convert it to a data URL for the thumbnail, and clean up resources
       */
      const handleSeeked = () => {
        try {
          // Create a canvas to capture the frame
          const canvas = document.createElement('canvas');
          canvas.width = tempVideo.videoWidth;
          canvas.height = tempVideo.videoHeight;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(tempVideo, 0, 0, canvas.width, canvas.height);
            const dataUrl = canvas.toDataURL('image/jpeg');
            setThumbnailDataUrl(dataUrl);

            // Clean up by removing the video source after capturing the thumbnail
            tempVideo.removeAttribute('src');
            tempVideo.load();
          }
        } catch {
          // Silently handle thumbnail generation errors
        }
      };

      // Set up event listeners before setting the source
      tempVideo.addEventListener('canplay', handleCanPlay);
      tempVideo.addEventListener('seeked', handleSeeked);
      tempVideo.addEventListener('error', () => {
        /*
         * Thumbnail generation will be skipped
         * Clean up to prevent memory leaks
         */
        tempVideo.removeEventListener('canplay', handleCanPlay);
        tempVideo.removeEventListener('seeked', handleSeeked);
      });

      // Set attributes for optimal performance
      tempVideo.muted = true; // Prevent audio playback
      tempVideo.preload = 'metadata'; // Only load video metadata, not the full content
      tempVideo.volume = 0; // Ensure no sound during thumbnail generation

      // Important: Only set the src now that we're ready to load
      tempVideo.src = videoUrl;

      // Return cleanup function
      return () => {
        tempVideo.removeEventListener('canplay', handleCanPlay);
        tempVideo.removeEventListener('seeked', handleSeeked);
        tempVideo.removeAttribute('src');
        tempVideo.load();
      };
    }

    // Return undefined for the case where we don't set up the video
    return undefined;
  }, [videoUrl, thumbnailDataUrl, isVideoExpanded]);
  useEffect(
  /* eslint-disable-next-line arrow-body-style --
     for readability, otherwise easy to miss this useEffect is only a cleanup function */
  () => {
    // Cleanup function to ensure body scrollbar is restored if component prematurely unmounts
    return () => {
      if (document.body && document.body.style.overflow === 'hidden') {
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
      }
    };
  }, []);

  // Early return if no video URL is provided
  if (!videoUrl) {
    return null;
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [!showPreview && /*#__PURE__*/_jsx(Styled.WithoutPreviewContainer, {
      alignXPreset: alignXPreset || 'center',
      ...rest,
      children: /*#__PURE__*/_jsxs(Styled.DownloadVideoButton, {
        onClick: handleDownload,
        disabled: isLoading,
        "data-id": "cp-social-video-standalone-download",
        cssBackgroundColor: setColorWithOpacityCss(cssBackgroundColor, cssBackgroundColorOpacity),
        cssBorderRadius: cssBorderRadius,
        cssBorderColor: cssBorderColor,
        cssBorderWidth: cssBorderWidth,
        cssColor: cssColor,
        children: [/*#__PURE__*/_jsx(FileDownload, {}), isLoading ? t('SocialLinks.downloadingButton', 'Downloading...') : downloadButtonLabel || t('SocialLinks.videoDownload', 'Download video')]
      })
    }), showPreview && videoUrl && /*#__PURE__*/_jsxs(Styled.VideoCard, {
      alignXPreset: alignXPreset || 'center',
      ...rest,
      children: [/*#__PURE__*/_jsxs(Styled.VideoThumbnailContainer, {
        role: "button",
        "aria-label": t('SocialLinks.videoThumbnailAlt', 'Preview campaign video'),
        tabIndex: 0,
        "data-id": "cp-social-video-preview-thumbnail",
        ref: refs.setReference,
        ...getReferenceProps({
          onClick: handlePlayClick
        }),
        children: [thumbnailDataUrl && /*#__PURE__*/_jsx("img", {
          src: thumbnailDataUrl,
          alt: ""
        }), /*#__PURE__*/_jsx(Styled.PlayCircleContainer, {
          children: /*#__PURE__*/_jsx(PlayCircle, {})
        })]
      }), /*#__PURE__*/_jsxs(Styled.VideoCardContent, {
        children: [previewTitle && /*#__PURE__*/_jsx(Styled.VideoCardTitle, {
          children: previewTitle
        }), previewDescription && /*#__PURE__*/_jsx(Styled.VideoCardDescription, {
          children: previewDescription
        }), showDownloadInPreview && /*#__PURE__*/_jsxs(Styled.DownloadVideoButton, {
          onClick: handleDownload,
          disabled: isLoading,
          "data-id": "cp-social-video-preview-download",
          cssBackgroundColor: setColorWithOpacityCss(cssBackgroundColor, cssBackgroundColorOpacity),
          cssBorderRadius: cssBorderRadius,
          cssBorderColor: cssBorderColor,
          cssBorderWidth: cssBorderWidth,
          cssColor: cssColor,
          children: [/*#__PURE__*/_jsx(FileDownload, {}), isLoading ? t('SocialLinks.downloadingButton', 'Downloading...') : t('SocialLinks.videoDownload', 'Download video')]
        })]
      })]
    }), /*#__PURE__*/_jsx(FloatingPortal, {
      children: isVideoExpanded && videoUrl && /*#__PURE__*/_jsx(Styled.FloatingVideoOverlay, {
        lockScroll: true,
        children: /*#__PURE__*/_jsx(FloatingFocusManager, {
          context: context,
          children: /*#__PURE__*/_jsxs("div", {
            ref: refs.setFloating,
            ...getFloatingProps(),
            children: [/*#__PURE__*/_jsx(Styled.Video, {
              children: /*#__PURE__*/_jsx("video", {
                ref: videoRef,
                controls: true
                /*
                 * Only set the src when the video is actually visible
                 * This prevents media keys from controlling a hidden video
                 */,
                src: isVideoExpanded ? videoUrl : undefined,
                poster: thumbnailDataUrl || undefined,
                "aria-label": t('SocialLinks.videoPreviewLabel', 'Campaign video preview'),
                "data-id": "cp-social-video-preview-video"
              })
            }), /*#__PURE__*/_jsx(Styled.CloseButton, {
              onClick: handleCloseVideo,
              "aria-label": t('SocialLinks.closeVideoButton', 'Close video'),
              children: /*#__PURE__*/_jsx(Close, {})
            })]
          })
        })
      })
    })]
  });
};